import {
  PolicyStatus,
  QuoteType,
  QuoteWorkflowStatus,
} from '@lockton-apps/shared/interfaces';

export const INFO_LEVELS = {
  Error: 'error',
  Warning: 'warning',
  Info: 'info',
  Success: 'success',
} as const;

export type InfoLevel = (typeof INFO_LEVELS)[keyof typeof INFO_LEVELS];

export interface WorkflowStatusMeta {
  label: string;
  icon: string;
  level: InfoLevel;
  supportingText?: string;
}

export interface WorkflowStatusQueueMeta {
  next: QuoteWorkflowStatus | null;
  upcoming: QuoteWorkflowStatus[];
}

export interface CombinedWorkflowStatusMeta {
  proposal: WorkflowStatusMeta;
  quote: WorkflowStatusMeta;
  payment: WorkflowStatusMeta;
}

interface ExtendableConfigDef<Config, VariantGroup extends string | number> {
  base: Config;
  overrides?: Partial<Record<VariantGroup, Partial<Config>>>;
}

export type ExtendableDefMap<
  Variant extends string | number,
  VariantGroup extends string | number,
  Config
> = Partial<
  Record<Variant | 'DEFAULT', ExtendableConfigDef<Config, VariantGroup>>
>;

export type ExtendableConfigLookup<
  Config,
  VariantGroup extends string | number,
  Variant extends string | number
> = Partial<Record<Variant, Record<VariantGroup, Config>>>;

export type ExtendableWorkflowConfigLookup<
  Variant extends string | number,
  VariantGroup extends string | number,
  Config
> = ExtendableConfigLookup<Config, VariantGroup, Variant>;

export type WorkflowConfigLookup = ExtendableConfigLookup<
  WorkflowStatusMeta,
  QuoteType,
  QuoteWorkflowStatus | 'DEFAULT'
>;

export type ConfigOverride<
  Variant extends string | number,
  VariantGroup extends string | number,
  Config
> = Partial<Record<Variant, Partial<Record<VariantGroup, Partial<Config>>>>>;

export type WorkflowConfigOverride = ConfigOverride<
  QuoteWorkflowStatus,
  QuoteType,
  WorkflowStatusMeta
>;

export type StatusQueueConfigOverride = ConfigOverride<
  QuoteWorkflowStatus,
  QuoteType,
  WorkflowStatusQueueMeta
>;

// type WorkflowMetaDef = ExtendableConfigDef<WorkflowStatusMeta, QuoteType>;

export type WorkflowMetaDefMap<Config> = ExtendableDefMap<
  QuoteWorkflowStatus,
  QuoteType,
  Config
>;

export type PolicyStatusMetaDefMap<Config> = ExtendableDefMap<
  PolicyStatus,
  'DEFAULT',
  Config
>;

export type PolicyStatusConfigOverride = ConfigOverride<
  PolicyStatus,
  'DEFAULT',
  WorkflowStatusMeta
>;
